"use client";

import { Tables } from "@/types/database";

type Content = Tables<'content'>;
type MasterSpeaker = Tables<'speakers'>;
type Certification = Tables<'certifications'>;
type Ticket = Tables<'tickets'>;
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import ResponsiveImage from "@/components/ui/ResponsiveImage";
import { formatDateToEuropean } from "@/lib/utils/date";
import { Calendar, Clock, MoreVertical, XCircle, Play } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/Badge";
import { serverDate } from "@/lib/time/serverClock";

interface ContentWithDetails extends Content {
  speakers?: MasterSpeaker[];
  tickets?: Ticket[];
  organizers?: { name: string; email: string; phone: string | null; image_url: string | null; description: string | null } | null;
}

type CardVariant = 'discovery' | 'organizer' | 'learner';

interface CourseCardProps {
  course: ContentWithDetails;
  onRegister?: (courseId: string) => void;
  isEnrolled?: boolean;
  variant?: CardVariant;

  // Organizer variant props
  onEdit?: () => void;
  onArchiveToggle?: () => void;
  onDelete?: () => void;
  onViewStudents?: () => void;
  onDuplicate?: () => void;
  onCancel?: () => void;
  onReactivate?: () => void;
  enrollmentCount?: number; // Used to determine Cancel vs Delete button

  // Learner variant props
  progressPercentage?: number | null;
  completedAt?: string | null;
  enrolledAt?: string | null;
  lastAccessedAt?: string | null;
  certifications?: Certification[];
  onViewCertificate?: () => void;

  // Override the card link destination (e.g. /bevent instead of /event)
  hrefOverride?: string;
}

export default function CourseCard({
  course,
  onRegister,
  isEnrolled = false,
  variant = 'discovery',
  onEdit,
  onArchiveToggle,
  onDelete,
  onViewStudents,
  onDuplicate,
  onCancel,
  onReactivate,
  enrollmentCount,
  progressPercentage,
  completedAt,
  enrolledAt,
  lastAccessedAt,
  certifications = [],
  onViewCertificate,
  hrefOverride,
}: CourseCardProps) {
  const router = useRouter();
  const { t } = useLanguage();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownTimerRef = useRef<NodeJS.Timeout | null>(null);

  const isEvent = course.type === 'event';
  const isDiscovery = variant === 'discovery';
  const isOrganizer = variant === 'organizer';
  const isLearner = variant === 'learner';

  // Determine if content is past (read-only)
  // Only events can be past - courses are always self_paced (no end date)
  const isPast = isEvent && course.end_date && new Date(course.end_date) < serverDate() && course.status === 'published';

  // Determine if event is cancelled
  const isCancelled = !!course.cancelled_at;

  // Determine left border color based on type
  const borderAccentClass = isEvent ? 'border-l-purple-600' : 'border-l-blue-600';

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (dropdownTimerRef.current) {
        clearTimeout(dropdownTimerRef.current);
      }
    };
  }, []);

  // Calculate the card href for the Link component
  const getCardHref = () => {
    const basePath = isEvent ? '/event' : '/courses';

    if (isDiscovery || isLearner) {
      // Discovery & Learner: navigate to course/event detail page
      return `${basePath}/${course.id}`;
    } else if (isOrganizer) {
      // Organizer: navigate to edit page (or view for past content)
      if (isPast) {
        return `${basePath}/${course.id}`;
      } else {
        return isEvent ? `/event/builder?edit=${course.id}` : `/course-builder?edit=${course.id}`;
      }
    }
    return `${basePath}/${course.id}`;
  };

  const cardHref = hrefOverride ?? getCardHref();

  const handleRegister = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const basePath = isEvent ? '/event' : '/courses';

    if (isEnrolled) {
      router.push(`${basePath}/${course.id}${isEvent ? '' : '/learn'}`);
    } else if (onRegister) {
      onRegister(course.id);
    } else {
      router.push(`${basePath}/${course.id}`);
    }
  };

  const handleContinueLearning = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const basePath = isEvent ? '/event' : '/courses';
    router.push(`${basePath}/${course.id}${isEvent ? '' : '/learn'}`);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return t('courseCard.formats.self_paced');
    return formatDateToEuropean(dateString);
  };

  const getFormatLabel = () => {
    // Courses are always self_paced, events use their event_format
    if (!isEvent) {
      return t('courseCard.formats.self_paced');
    }
    const format = course.event_format;
    switch (format) {
      case 'online': return t('courseCard.formats.online');
      case 'in_person': return t('courseCard.formats.in_person');
      case 'hybrid': return t('courseCard.formats.hybrid');
      default: return t('courseCard.formats.online');
    }
  };

  const getInstructorName = () => {
    const firstSpeaker = course.speakers?.[0];
    if (firstSpeaker) {
      // Use full_name if available, otherwise construct from first_name and last_name
      if (firstSpeaker.full_name) {
        return firstSpeaker.full_name;
      }
      if (firstSpeaker.first_name && firstSpeaker.last_name) {
        return `${firstSpeaker.first_name} ${firstSpeaker.last_name}`;
      }
    }

    return course.organizers?.name || null;
  };

  const handleDropdownEnter = () => {
    if (dropdownTimerRef.current) {
      clearTimeout(dropdownTimerRef.current);
      dropdownTimerRef.current = null;
    }
    setShowDropdown(true);
  };

  const handleDropdownLeave = () => {
    dropdownTimerRef.current = setTimeout(() => {
      setShowDropdown(false);
    }, 300);
  };

  const hasIssuedCertificate = certifications.some(c => c.status === 'issued');

  // Get the lowest price from available tickets
  const getLowestPrice = () => {
    if (!course.tickets || course.tickets.length === 0) {
      return null;
    }

    const availableTickets = course.tickets.filter(
      ticket => ticket.status === 'available' && !ticket.requires_promocode
    );

    if (availableTickets.length === 0) {
      return null;
    }

    const lowestPriceTicket = availableTickets.reduce((lowest, ticket) => {
      const ticketPrice = Number(ticket.price) || 0;
      const lowestPrice = Number(lowest.price) || 0;
      return ticketPrice < lowestPrice ? ticket : lowest;
    });

    return lowestPriceTicket;
  };

  const lowestPriceTicket = getLowestPrice();

  const formatPrice = (price: number | string, currency: string = 'UAH') => {
    const numPrice = Number(price);
    if (numPrice === 0) {
      return t('courseCard.free') || 'Free';
    }
    // Format with thousand separators
    const formatted = numPrice.toLocaleString('uk-UA');
    return `${formatted} ${currency}`;
  };

  return (
    <Link
      href={cardHref}
      className={`bg-white rounded-lg border border-gray-200 ${borderAccentClass} border-l-4 hover:border-blue-300 hover:shadow-lg shadow-sm transition-all duration-200 group flex flex-col h-full cursor-pointer${isDiscovery && isPast ? ' opacity-75' : ''}`}
    >
      {/* Image with responsive height and progress overlay */}
      <div className="relative flex-shrink-0">
        <ResponsiveImage
          src={course.image_url || '/event-placeholder.svg'}
          alt={course.title}
          variant="card"
          hoverScale
          shape="none"
          containerClassName="rounded-t-lg"
        />

        {/* Progress overlay for learner variant - courses only */}
        {isLearner && !isEvent && progressPercentage !== null && progressPercentage !== undefined && (
          <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-lg">
            <span className="text-xs font-bold text-gray-900">{progressPercentage}% {t('dashboard.complete')}</span>
          </div>
        )}

        {/* Past badge for discovery variant */}
        {isDiscovery && isPast && (
          <div className="absolute top-3 right-3 bg-gray-800/80 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
            {t('courseCard.past')}
          </div>
        )}
      </div>

      <div className="p-5 flex flex-col flex-grow bg-gray-50 rounded-b-lg">
        <div className="flex-grow">
          {/* Title and Badges */}
          <div className="flex items-start justify-between gap-2 mb-2">
            <h3 className="text-sm md:text-base font-semibold text-gray-900 flex-1 break-words min-w-0">
              {course.title}
            </h3>
            <div className="flex flex-col gap-1 flex-shrink-0">
              {/* Status badges for organizer variant */}
              {isOrganizer && (
                <>
                  <Badge variant="info" shape="pill" className="font-semibold">
                    {t('dashboard.creator')}
                  </Badge>
                  {isCancelled ? (
                    <Badge variant="error" shape="pill" icon={<XCircle className="h-3 w-3" />} className="font-semibold">
                      {t('dashboard.cancellation.eventCancelled')}
                    </Badge>
                  ) : (
                    <Badge
                      variant={
                        course.status === 'published'
                          ? 'success'
                          : course.status === 'archived'
                            ? 'default'
                            : 'warning'
                      }
                      shape="pill"
                      className="font-semibold"
                    >
                      {t(`dashboard.${course.status || 'draft'}`)}
                    </Badge>
                  )}
                </>
              )}

              {/* Learner badge */}
              {isLearner && (
                <>
                  <Badge variant="purple" shape="pill" className="font-semibold">
                    {t('dashboard.learner')}
                  </Badge>
                  <Badge
                    variant={completedAt ? 'success' : 'info'}
                    shape="pill"
                    className="font-semibold"
                  >
                    {completedAt ? t('dashboard.completed') : t('dashboard.inProgress')}
                  </Badge>
                </>
              )}
            </div>
          </div>

          {/* Instructor - only for discovery variant */}
          {isDiscovery && getInstructorName() && (
            <p className="text-sm text-gray-600 mb-3">{getInstructorName()}</p>
          )}

          {/* Price - only for discovery variant */}
          {isDiscovery && lowestPriceTicket && (
            <div className="mb-3">
              <div className="flex items-baseline gap-2">
                <span className="text-lg font-bold text-gray-900">
                  {formatPrice(lowestPriceTicket.price, lowestPriceTicket.currency || 'UAH')}
                </span>
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="space-y-1.5 mb-4 text-sm text-gray-600">
            {/* Discovery variant shows start date and format */}
            {isDiscovery && (
              <>
                {course.start_date && (
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {formatDate(course.start_date)}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {getFormatLabel()}
                </div>
              </>
            )}

            {/* Organizer variant shows created and start date */}
            {isOrganizer && (
              <>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span>{t('dashboard.created')}: {formatDate(course.created_at)}</span>
                </div>
                {course.start_date && (
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span>{t('dashboard.starts')}: {formatDate(course.start_date)}</span>
                  </div>
                )}
              </>
            )}

            {/* Learner variant shows enrolled date and last accessed */}
            {isLearner && (
              <>
                {enrolledAt && (
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span>{t('dashboard.enrolled')}: {formatDate(enrolledAt)}</span>
                  </div>
                )}
                {completedAt && (
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span>{t('dashboard.completed')}: {formatDate(completedAt)}</span>
                  </div>
                )}
                {lastAccessedAt && (
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="text-xs">{t('dashboard.lastAccessed')}: {formatDate(lastAccessedAt)}</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Progress bar and Action Buttons - stick to bottom */}
        <div className="mt-auto">
          {/* Progress bar for learner variant - courses only */}
          {isLearner && !isEvent && progressPercentage !== null && progressPercentage !== undefined && (
            <div className="mb-4">
              <div className="flex justify-between text-sm text-gray-600 mb-1.5">
                <span className="font-medium">{t('dashboard.progress')}</span>
                <span className="font-semibold">{progressPercentage}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className={`${progressPercentage === 100 ? 'bg-green-600' : progressPercentage >= 76 ? 'bg-blue-600' : progressPercentage >= 26 ? 'bg-yellow-500' : 'bg-red-500'} h-3 rounded-full transition-all duration-300`}
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            {/* Discovery variant - registration button */}
            {isDiscovery && (
              <Button
                variant="primary"
                className="w-full"
                onClick={handleRegister}
              >
                {isEnrolled
                  ? (isEvent
                    ? t('courseCard.goToEvent')
                    : completedAt
                      ? t('courseCard.courseCompleted')
                      : (progressPercentage || 0) > 0
                        ? t('courseCard.continueLearning')
                        : t('courseCard.startLearning')
                  )
                  : (isEvent
                    ? (isPast && !lowestPriceTicket ? t('courseCard.viewEvent') : t('courseCard.registerForEvent'))
                    : t('courseCard.registerForCourse'))
                }
              </Button>
            )}

            {/* Organizer variant - edit/view and more dropdown */}
            {isOrganizer && (
              <>
                <Button
                  variant="primary"
                  className="flex-1 min-w-0 truncate"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onEdit?.();
                  }}
                >
                  {isEvent ? t('enhancedDashboard.editEvent') : t('enhancedDashboard.editCourse')}
                </Button>
                <div className="relative flex-shrink-0">
                  <Button
                    variant="secondary"
                    size="sm"
                    onMouseEnter={handleDropdownEnter}
                    onMouseLeave={handleDropdownLeave}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowDropdown(!showDropdown);
                    }}
                    aria-label={t('dashboard.more')}
                  >
                    <MoreVertical className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('dashboard.more')}</span>
                  </Button>
                  {showDropdown && (
                    <div
                      onMouseEnter={handleDropdownEnter}
                      onMouseLeave={handleDropdownLeave}
                      className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-50"
                    >
                      <Button
                        variant="ghost"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onViewStudents?.();
                          setShowDropdown(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 justify-start h-auto rounded-none first:rounded-t-lg"
                      >
                        {t('enhancedDashboard.students')}
                      </Button>
                      {isEvent && onDuplicate && (
                        <Button
                          variant="ghost"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onDuplicate();
                            setShowDropdown(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 justify-start h-auto rounded-none"
                        >
                          {t('dashboard.duplicate') || 'Duplicate Event'}
                        </Button>
                      )}
                      {/* Reactivate for cancelled events */}
                      {isEvent && !isPast && isCancelled && onReactivate && (
                        <Button
                          variant="ghost"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onReactivate();
                            setShowDropdown(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50 flex items-center gap-2 justify-start h-auto rounded-none"
                        >
                          <Play className="h-4 w-4" />
                          {t('dashboard.cancellation.reactivateEvent')}
                        </Button>
                      )}
                      {/* Hide archive and delete for past/cancelled content */}
                      {!isPast && !isCancelled && (
                        <>
                          <Button
                            variant="ghost"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onArchiveToggle?.();
                              setShowDropdown(false);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 justify-start h-auto rounded-none"
                          >
                            {course.status === 'archived' ? t('dashboard.unarchive') : t('dashboard.archive')}
                          </Button>
                          <div className="border-t border-gray-200"></div>
                          {/* Show Cancel if has enrollments, Delete if no enrollments */}
                          {(enrollmentCount ?? 0) > 0 && onCancel ? (
                            <Button
                              variant="ghost"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onCancel();
                                setShowDropdown(false);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 last:rounded-b-lg flex items-center gap-2 justify-start h-auto rounded-none"
                            >
                              <XCircle className="h-4 w-4" />
                              {t('dashboard.cancellation.cancelEvent')}
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onDelete?.();
                                setShowDropdown(false);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 last:rounded-b-lg justify-start h-auto rounded-none"
                            >
                              {t('dashboard.delete')}
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Learner variant - continue learning and certificate */}
            {isLearner && (
              <>
                <Button
                  variant="primary"
                  className="flex-1 min-w-0 truncate"
                  onClick={handleContinueLearning}
                >
                  {isEvent
                    ? t('event.viewEvent')
                    : completedAt
                      ? t('dashboard.courseCompleted')
                      : (progressPercentage || 0) > 0
                        ? t('dashboard.continueLearning')
                        : t('dashboard.startLearning')
                  }
                </Button>
                {hasIssuedCertificate && onViewCertificate && (
                  <Button
                    variant="secondary"
                    className="flex-shrink-0 whitespace-nowrap"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onViewCertificate();
                    }}
                  >
                    {t('dashboard.viewCertificate')}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
